/* eslint-disable jsx-a11y/media-has-caption */
import { IconButton, Slider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RoundedButton from '@src/components/UI/RoundedButton';
import { primary } from '@src/muithemes/b2ChatTheme';
import { getTimeDuration } from '@src/utils/mediaUtils';
import { MutableRefObject, useRef, useState } from 'react';

type colorType = 'primary' | 'secondary' | 'inherit';
interface ChatMessageAudioProps {
  mediaUrl: string;
  color?: colorType;
}

const MAX_PROGRESS = 100;

const ChatMessageAudio: React.FC<ChatMessageAudioProps> = ({
  mediaUrl,
  color = 'primary',
}) => {
  const classes = useStyles();
  const audioRef = useRef() as MutableRefObject<HTMLAudioElement>;
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [speed, setSpeed] = useState(1);

  const progress = duration > 0 ? (currentTime / duration) * MAX_PROGRESS : 0;

  // eslint-disable-next-line consistent-return
  const onLoadedAudio = () => {
    if (!audioRef.current) return;

    audioRef.current.currentTime = currentTime;
    audioRef.current.playbackRate = 1.0;
    setSpeed(1);
    setDuration(audioRef.current?.duration);
  };

  const onUpdateAudio = () => {
    setCurrentTime(audioRef.current?.currentTime ?? 0);
  };

  const onPlay = () => {
    if (!audioRef.current) return;

    if (!audioRef.current.paused) {
      audioRef.current.pause();
      return;
    }

    const FINISHED_THRESHOLD = 0.01;
    const isFinished =
      Math.abs(audioRef.current.currentTime - audioRef.current.duration) <
      FINISHED_THRESHOLD;

    if (isFinished) {
      setCurrentTime(0);
      audioRef.current.currentTime = 0;
    }

    document.querySelectorAll('.record-audio').forEach(audio => {
      if (audio !== audioRef.current && 'pause' in audio)
        (audio as HTMLAudioElement).pause();
    });

    audioRef.current.play();
  };

  const onChangeTime = (rangePercentage: number) => {
    const newTime = (duration / MAX_PROGRESS) * rangePercentage;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const onChangeSpeed = () => {
    if (audioRef.current?.playbackRate) {
      const newSpeed = speed < 2 ? speed + 0.5 : 1;
      audioRef.current.playbackRate = newSpeed;
      setSpeed(newSpeed);
    }
  };

  return (
    <>
      <div className={`${classes.container} is-${color}`}>
        <RoundedButton onClick={onPlay} className={classes.controlButton}>
          {isPlaying ? (
            <PauseIcon fontSize="inherit" />
          ) : (
            <PlayArrowIcon fontSize="inherit" />
          )}
        </RoundedButton>
        <div className={classes.actionsContainer}>
          <Slider
            value={progress}
            onChange={(_, newValue) => onChangeTime(Number(newValue))}
            max={MAX_PROGRESS}
            className={classes.range}
          />
          <div className={classes.timeContainer}>
            {getTimeDuration(currentTime)} / {getTimeDuration(duration)}
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onChangeSpeed}
          className={classes.speedButton}
        >
          {speed}x
        </IconButton>
      </div>
      <audio
        className="record-audio"
        controls
        ref={audioRef}
        src={mediaUrl}
        preload="metadata"
        style={{ display: 'none' }}
        onLoadedData={onLoadedAudio}
        onTimeUpdate={onUpdateAudio}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
    </>
  );
};

export default ChatMessageAudio;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: 'auto',
    height: 40,
    marginBottom: -12,
    marginTop: -2,
    width: '100%',
    '&.is-primary': {
      '--audio-color': primary.main,
      '--audio-contrast': '#fff',
      '--audio-rail': 'rgba(0,0,0,0.15)',
    },
    '&.is-secondary': {
      '--audio-color': '#fff',
      '--audio-contrast': '#323444',
      '--audio-rail': 'rgba(255,255,255,0.40)',
    },
    '&.is-inherit': {
      '--audio-color': 'currentColor',
      '--audio-contrast': '#fff',
      '--audio-rail': 'rgba(0,0,0,0.10)',
      '& $controlButton': {
        color: 'currentColor',
        backgroundColor: 'currentColor',
        '& .MuiSvgIcon-root': { color: '#fff' },
        '&:hover': { opacity: 0.8 },
      },
      '& $range': {
        color: 'currentColor',
        '& .MuiSlider-track': { backgroundColor: 'currentColor' },
        '& .MuiSlider-thumb': { backgroundColor: 'currentColor' },
      },
    },
  },
  actionsContainer: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    flex: 1,
    marginLeft: 12,
  },
  timeContainer: {
    top: 15,
    position: 'absolute',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  controlButton: {
    boxShadow: 'none',
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    fontSize: '1.2rem',
    backgroundColor: 'var(--audio-color)',
    '& .MuiSvgIcon-root': {
      color: 'var(--audio-contrast)',
    },
    '&:hover': {
      opacity: 0.8,
      backgroundColor: 'var(--audio-color)',
    },
  },
  range: {
    '&.MuiSlider-root': { minWidth: 200 },
    '& .MuiSlider-rail': {
      height: 6,
      backgroundColor: 'var(--audio-rail)',
    },
    '& .MuiSlider-track': {
      height: 6,
      backgroundColor: 'var(--audio-color)',
    },
    '& .MuiSlider-thumb': {
      borderWidth: 0,
      marginTop: -3,
      width: 12,
      height: 12,
      marginLeft: 0,
      transform: 'translateX(-6px)',
      backgroundColor: 'var(--audio-color)',
    },
    '& .MuiSlider-thumb.MuiSlider-active::after': {
      backgroundColor: 'var(--audio-color)',
      opacity: 0.2,
    },
    '@media (pointer: coarse)': { padding: 0 },
  },
  speedButton: {
    width: 40,
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--audio-color)',
  },
});
